import {
    ActionIcon,
    Button,
    Divider,
    Fieldset,
    Group,
    Input,
    Select,
    SimpleGrid,
    Stack,
    Tabs,
    Text,
    Textarea,
    TextInput,
    ThemeIcon,
    Title,
} from "@mantine/core";
import { Dropzone } from "@mantine/dropzone";
import { modals } from "@mantine/modals";
import { IconExclamationCircle, IconPlus, IconTrash, IconUpload } from "@tabler/icons-react";
import { Suspense, useEffect, useState } from "react";
import { fileApi } from "shared/api/fileApi";
import { FileItem } from "shared/components/global/FileItem";
import { ScribeIcon } from "shared/components/global/Scribe/ScribeIcon";
import { Section, SectionProvider } from "shared/components/global/Section";
import { useIpawsAlertFormContext } from "shared/components/ipaws/alert/context";
import { useIpawsAlertFormAllowedEventCodes, useIpawsAlertFormAllowedGeoCodes } from "shared/components/ipaws/alert/query";
import { useFormState } from "shared/hooks/useFormState";
import { arr } from "shared/utils/array";
import { titlecase, to } from "shared/utils/fns";
import { notify } from "shared/utils/notify";
import { useUploadQueue } from "shared/utils/upload";
import {
    IpawsCapMessageInfoCategory,
    IpawsCapMessageInfoCertainty,
    IpawsCapMessageInfoSeverity,
    IpawsCapMessageInfoUrgency,
    IpawsChannel,
    Language,
} from "src/gql/graphql";
import { LanguageSchema } from "src/gql/zod";

export const IpawsAlertFormContent = () => {
    const form = useIpawsAlertFormContext();
    const [activeLang, setActiveLang] = useState(Language.English);
    const [contents, setContents] = useFormState(form, "contents");
    const [channels] = useFormState(form, "channels");
    const [, setAllowedEventCodes] = useFormState(form, "meta.allowedEventCodes");
    const [, setAllowedFipsCodes] = useFormState(form, "meta.allowedGeocodes");

    const activeContentIdx = contents.findIndex((info) => info.language == activeLang);
    const [easAudioFileId, setEasAudioFileId] = useFormState(form, `contents.${activeContentIdx}.easAudioFileId`);

    const { data: eventCodes } = useIpawsAlertFormAllowedEventCodes();
    const { data: fipsCodes } = useIpawsAlertFormAllowedGeoCodes();

    useEffect(() => {
        if (!!eventCodes) setAllowedEventCodes(eventCodes.map(to("id")));
        if (!!fipsCodes) setAllowedFipsCodes(fipsCodes.map(to("code")));
    }, [eventCodes, fipsCodes]);

    return (
        <SectionProvider>
            <Section
                label={
                    <Group gap="xs">
                        <Title order={2} size="h4">
                            CAP Fields
                        </Title>
                        <ScribeIcon src="https://scribehow.com/embed/CAP_field_definitions__bCOBkCNqSBOMmAIgI713tA" />
                    </Group>
                }
            >
                <SimpleGrid cols={{ xs: 1, sm: 2 }}>
                    <Select
                        label="Category"
                        required
                        data={Object.values(IpawsCapMessageInfoCategory)}
                        {...form.getInputProps("category")}
                    />

                    <Select
                        label="Severity"
                        required
                        data={[
                            IpawsCapMessageInfoSeverity.Extreme,
                            IpawsCapMessageInfoSeverity.Severe,
                            IpawsCapMessageInfoSeverity.Moderate,
                            IpawsCapMessageInfoSeverity.Minor,
                            IpawsCapMessageInfoSeverity.Unknown,
                        ]}
                        {...form.getInputProps("severity")}
                    />

                    <Select
                        label="Urgency"
                        required
                        data={arr(Object.values(IpawsCapMessageInfoUrgency))
                            .except(IpawsCapMessageInfoUrgency.Unknown)
                            .append(IpawsCapMessageInfoUrgency.Unknown)
                            .take()}
                        {...form.getInputProps("urgency")}
                    />

                    <Select
                        label="Certainty"
                        required
                        data={[
                            IpawsCapMessageInfoCertainty.Observed,
                            IpawsCapMessageInfoCertainty.Likely,
                            IpawsCapMessageInfoCertainty.Possible,
                            IpawsCapMessageInfoCertainty.Unlikely,
                            IpawsCapMessageInfoCertainty.Unknown,
                        ]}
                        {...form.getInputProps("certainty")}
                    />
                </SimpleGrid>
            </Section>
            <Section
                label={
                    <Group gap="xs">
                        <Title order={2} size="h4">
                            Message Content{" "}
                        </Title>
                        <ScribeIcon src="https://scribehow.com/embed/Message_content_definitions__iO2mkQ-0S3uz5NS1poLBGg" />
                    </Group>
                }
            >
                <Tabs
                    value={activeLang}
                    onChange={(next) => {
                        if (!next || next.startsWith("$$")) return;
                        setActiveLang(LanguageSchema.parse(next));
                    }}
                >
                    <Tabs.List>
                        {contents.map((info, i) => (
                            <Tabs.Tab
                                key={info.language}
                                value={info.language}
                                leftSection={
                                    Object.keys(form.errors).some((k) => k.startsWith(`contents.${i}`)) && (
                                        <ThemeIcon variant="transparent" c="var(--mantine-color-error)">
                                            <IconExclamationCircle size={16} />
                                        </ThemeIcon>
                                    )
                                }
                                rightSection={
                                    info.language != Language.English && (
                                        <ActionIcon
                                            variant="transparent"
                                            size="xs"
                                            onClick={() =>
                                                void notify
                                                    .confirm("Are you sure you want to remove this content?", {
                                                        title: "Remove Content",
                                                    })
                                                    .then(({ close }) => {
                                                        const nextContents = arr(contents).eqBy("language").except(info).take();
                                                        if (nextContents.length > 0) setActiveLang(nextContents[0].language);
                                                        setContents(nextContents);
                                                        close();
                                                    })
                                            }
                                        >
                                            <IconTrash size={16} />
                                        </ActionIcon>
                                    )
                                }
                            >
                                {titlecase(info.language)}
                            </Tabs.Tab>
                        ))}

                        {!contents.map(to("language")).includes(Language.Spanish) && (
                            <Tabs.Tab
                                value="$$add-spanish"
                                leftSection={<IconPlus size={16} />}
                                onClick={() => {
                                    setContents(
                                        contents.concat([
                                            {
                                                language: Language.Spanish,
                                                contactInfo: "",
                                                description: "",
                                                headline: "",
                                                instructions: "",
                                                wea: { longText: "", shortText: "" },
                                            },
                                        ]),
                                    );
                                    setActiveLang(Language.Spanish);
                                }}
                            >
                                Add Spanish
                            </Tabs.Tab>
                        )}
                    </Tabs.List>
                </Tabs>

                <Stack gap="sm">
                    <TextInput label="Headline" required {...form.getInputProps(`contents.${activeContentIdx}.headline`)} />
                    <Textarea
                        label="Description"
                        required
                        minRows={3}
                        autosize
                        {...form.getInputProps(`contents.${activeContentIdx}.description`)}
                    />
                    <Textarea
                        label="Instruction"
                        minRows={3}
                        autosize
                        {...form.getInputProps(`contents.${activeContentIdx}.instructions`)}
                    />

                    {channels.includes(IpawsChannel.Eas) && (
                        <Input.Wrapper error={form.getInputProps(`contents.${activeContentIdx}.easAudioFileId`).error}>
                            <Stack gap="xs">
                                <Input.Label>EAS Audio</Input.Label>
                                <Group>
                                    {!easAudioFileId && (
                                        <Button
                                            variant="default"
                                            onClick={() =>
                                                modals.open({
                                                    title: <Title order={3}>Upload EAS Audio</Title>,
                                                    children: (
                                                        <UploadEasAudioPanel
                                                            onConfirm={(fileId) => {
                                                                setEasAudioFileId(fileId);
                                                                modals.closeAll();
                                                            }}
                                                        />
                                                    ),
                                                })
                                            }
                                        >
                                            Upload Audio File
                                        </Button>
                                    )}
                                    {!!easAudioFileId && (
                                        <Suspense>
                                            <Stack gap="xs">
                                                <FileItem fileId={easAudioFileId}>
                                                    <FileItem.Action>
                                                        <ActionIcon
                                                            variant="transparent"
                                                            onClick={() =>
                                                                void notify
                                                                    .confirm("Are you sure you want to remove this audio?")
                                                                    .then(({ close }) => {
                                                                        return fileApi.delete(easAudioFileId).then(close);
                                                                    })
                                                                    .then(() => setEasAudioFileId(undefined))
                                                            }
                                                        >
                                                            <IconTrash size={16} />
                                                        </ActionIcon>
                                                    </FileItem.Action>
                                                </FileItem>
                                            </Stack>
                                        </Suspense>
                                    )}
                                </Group>
                                <Input.Error />
                            </Stack>
                        </Input.Wrapper>
                    )}

                    {channels.includes(IpawsChannel.Cmas) && (
                        <Fieldset legend={<Text fw="bold">WEA Message Content</Text>}>
                            <Stack>
                                <Group justify="flex-start">
                                    <Button
                                        variant="default"
                                        onClick={() =>
                                            void notify
                                                .confirm(
                                                    "Are you sure you want to replace the WEA content with the headline and description? This will override any existing WEA content.",
                                                )
                                                .then(({ close }) => {
                                                    form.setFieldValue(
                                                        `contents.${activeContentIdx}.wea.shortText`,
                                                        form.values.contents[activeContentIdx].headline,
                                                    );
                                                    form.setFieldValue(
                                                        `contents.${activeContentIdx}.wea.longText`,
                                                        form.values.contents[activeContentIdx].description,
                                                    );
                                                    close();
                                                })
                                        }
                                    >
                                        Use Message Content
                                    </Button>
                                </Group>
                                <TextInput
                                    label="Short Text"
                                    required
                                    description={`${90 - form.getInputProps(`contents.${activeContentIdx}.wea.shortText`).value?.length} characters remaining`}
                                    maxLength={90}
                                    {...form.getInputProps(`contents.${activeContentIdx}.wea.shortText`)}
                                />
                                <Textarea
                                    label="Long Text"
                                    required
                                    autosize
                                    minRows={2}
                                    maxLength={360}
                                    description={`${360 - form.getInputProps(`contents.${activeContentIdx}.wea.longText`).value?.length} characters remaining`}
                                    {...form.getInputProps(`contents.${activeContentIdx}.wea.longText`)}
                                />
                            </Stack>
                        </Fieldset>
                    )}
                </Stack>
            </Section>
        </SectionProvider>
    );
};

/** @private */
const UploadEasAudioPanel = (props: { onConfirm: (fileId: string) => void }) => {
    const [draft, setDraft] = useState<{ id: string; fileName: string }>();

    const uploader = useUploadQueue({
        usage: "ipaws",
        onUploaded([{ id, fileName }]) {
            setDraft({ id, fileName });
        },
    });

    return (
        <Stack>
            {!draft && (
                <Dropzone loading={uploader.isLoading} accept={["audio/mpeg"]} onDrop={([file]) => uploader.enqueue(file)}>
                    <Stack align="center" justify="center" p="xl">
                        <IconUpload size={32} />
                        <div style={{ textAlign: "center" }}>
                            <Text size="lg" inline>
                                Upload Audio File
                            </Text>
                            <Text size="sm" c="dimmed" inline mt={7}>
                                mp3
                            </Text>
                        </div>
                    </Stack>
                </Dropzone>
            )}
            <Suspense>
                {!!draft && (
                    <FileItem fileId={draft.id}>
                        <FileItem.Action>
                            <ActionIcon
                                aria-label="Delete audio"
                                variant="transparent"
                                onClick={() => {
                                    void fileApi.delete(draft.id);
                                    setDraft(undefined);
                                }}
                            >
                                <IconTrash size={16} />
                            </ActionIcon>
                        </FileItem.Action>
                    </FileItem>
                )}
            </Suspense>
            <Divider mx="-md" />
            <Group justify="flex-end">
                <Button disabled={!draft} onClick={() => props.onConfirm(draft!.id)}>
                    Confirm
                </Button>
            </Group>
        </Stack>
    );
};
